import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../api/axiosConfig';

const createDevice = async (data) => axios.post('/devices', data);
const updateDevice = async ({ id, data }) => axios.put(`/devices/${id}`, data);

const deviceTypes = [
  'Навигационный прибор',
  'ДУТ проводной',
  'ДУТ BLE',
  'Датчик',
  'Камера'
];

const deviceStatuses = [
  'Установлен',
  'Неисправен',
  'В ремонте',
  'Снят'
];

const fetchVehicles = async () => {
  const { data } = await axios.get('/vehicles');
  return data;
};

const DeviceForm = ({ open, onClose, device, onSuccess }) => {
  const [form, setForm] = useState({
    imei: '',
    sim: '',
    type: 'Навигационный прибор',
    model: '',
    serialNumber: '',
    vehicleId: null,
    status: 'Установлен',
  });
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  const { data: vehicles, isLoading: vehiclesLoading } = useQuery({
    queryKey: ['vehicles'],
    queryFn: fetchVehicles,
    enabled: open,
  });

  useEffect(() => {
    if (device && open) {
      setForm({
        imei: device.imei || '',
        sim: device.sim || '',
        type: device.type || 'Навигационный прибор',
        model: device.model || '',
        serialNumber: device.serialNumber || '',
        vehicleId: device.vehicleId?._id || device.vehicleId || null,
        status: device.status || 'Установлен',
      });
    } else if (open) {
      setForm({
        imei: '',
        sim: '',
        type: 'Навигационный прибор',
        model: '',
        serialNumber: '',
        vehicleId: null,
        status: 'Установлен',
      });
    }
  }, [device, open]);

  const mutation = useMutation({
    mutationFn: device ? updateDevice : createDevice,
    onSuccess: () => {
      queryClient.invalidateQueries(['devices']);
      if (onSuccess) onSuccess();
      if (onClose) onClose();
    },
    onError: (err) => {
      console.error('Save error:', err);
      setError(err.response?.data?.message || 'Error saving device');
    },
  });

  const handleVehicleChange = (newVehicleId) => {
    setForm(prev => ({
      ...prev,
      vehicleId: newVehicleId,
      // Если выбираем транспорт, ставим статус "Установлен", иначе оставляем прежний
      status: newVehicleId ? 'Установлен' : prev.status
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.imei) {
      setError('IMEI is required');
      return;
    }
    mutation.mutate(device ? { id: device._id, data: form } : form);
  };

  if (!open) return null;

  const selectedVehicle = vehicles?.find(v => v._id === form.vehicleId) || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{device ? 'Edit Device' : 'Add Device'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="IMEI"
                required
                value={form.imei}
                onChange={(e) => setForm({ ...form, imei: e.target.value })}
                helperText="Unique device identifier (required)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SIM Card Number"
                value={form.sim}
                onChange={(e) => setForm({ ...form, sim: e.target.value })}
                helperText="SIM card number for cellular connectivity (optional)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  label="Type"
                >
                  {deviceTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Model"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Serial Number"
                value={form.serialNumber}
                onChange={(e) => setForm({ ...form, serialNumber: e.target.value })}
                helperText="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={vehicles || []}
                getOptionLabel={(option) => `${option.licensePlate} - ${option.brand} ${option.model}`}
                value={selectedVehicle}
                onChange={(event, newValue) => {
                  handleVehicleChange(newValue?._id || null);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vehicle"
                    placeholder="Search by license plate..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {vehiclesLoading && <CircularProgress color="inherit" size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option._id === value?._id}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  label="Status"
                >
                  {deviceStatuses.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default DeviceForm;